import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

const API = import.meta.env.PROD
  ? 'https://cardealeros-api.bowling-anthony.workers.dev/v1'
  : '/v1';

interface VehicleDetail {
  id: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  vin: string | null;
  body_style: string | null;
  mileage: number | null;
  asking_price_cents: number | null;
  market_value_cents: number | null;
  exterior_color: string | null;
  interior_color: string | null;
  condition: string | null;
  title: string | null;
  description: string | null;
  features: string | null;
  engine: string | null;
  transmission: string | null;
  drivetrain: string | null;
  fuel_type: string | null;
  doors: number | null;
  created_at: string;
  dealer_name: string | null;
  dealer_phone: string | null;
  dealer_city: string | null;
  dealer_state: string | null;
  seller_name: string | null;
  seller_phone: string | null;
  seller_city: string | null;
  seller_state: string | null;
  photos: Array<{ id: string; url: string; thumbnail_url: string | null; position: number }>;
  marketplace_description?: string;
  highlights?: string[];
  known_issues?: string[];
  overall_grade?: string;
  investment_rating?: string;
  estimated_days_to_sell?: number;
}

interface RelatedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  mileage: number | null;
  asking_price_cents: number | null;
  photo_url: string | null;
  dealer_city: string | null;
  dealer_state: string | null;
}

function formatPrice(cents: number | null) {
  if (!cents) return '';
  return '$' + (cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatMiles(miles: number | null) {
  if (!miles) return '';
  return miles.toLocaleString() + ' mi';
}

function SpecChip({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-2">
      <p className="text-surface-500 text-[10px] uppercase tracking-wider">{label}</p>
      <p className="text-white text-sm font-medium truncate">{value}</p>
    </div>
  );
}

export default function ListingPage() {
  const { id } = useParams<{ id: string }>();
  const [vehicle, setVehicle] = useState<VehicleDetail | null>(null);
  const [related, setRelated] = useState<RelatedVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showCalc, setShowCalc] = useState(false);
  const [downPayment, setDownPayment] = useState(0);
  const [rate, setRate] = useState(7.9);
  const [term, setTerm] = useState(60);
  const [showPhone, setShowPhone] = useState(false);
  const [showBosModal, setShowBosModal] = useState(false);
  const [bosBuyer, setBosBuyer] = useState({ name: '', address: '', city: '', state: '', zip: '', phone: '', drivers_license: '' });

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setPhotoIdx(0);

    fetch(`${API}/public/vehicles/${id}`)
      .then((r) => r.json())
      .then(async (res: any) => {
        if (res.success) {
          const v = res.data;
          // Try to get enrichment data
          try {
            const enRes = await fetch(`${API}/public/vehicles/${id}/enrichment`);
            const enJson = await enRes.json() as any;
            if (enJson.success && enJson.data) {
              v.marketplace_description = enJson.data.enrichment?.marketplace_description;
              v.highlights = enJson.data.enrichment?.highlights;
              v.known_issues = enJson.data.enrichment?.known_issues;
              v.vehicle_badge = enJson.data.enrichment?.vehicle_badge;
              v.market_position = enJson.data.enrichment?.market_position;
              v.estimated_days_to_sell = enJson.data.enrichment?.estimated_days_to_sell;
            }
          } catch { /* enrichment optional */ }
          setVehicle(v);

          // Fetch related
          if (v.make) {
            fetch(`${API}/public/vehicles?make=${encodeURIComponent(v.make)}&limit=4`)
              .then((r) => r.json())
              .then((relRes: any) => {
                if (relRes.success) {
                  setRelated(relRes.data.filter((r: any) => r.id !== id).slice(0, 3));
                }
              })
              .catch(() => {});
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-900 text-white">
        <header className="px-4 py-4">
          <Link to="/browse" className="text-surface-400 hover:text-white text-sm">&larr; Back to browse</Link>
        </header>
        <div className="max-w-3xl mx-auto px-4">
          <div className="aspect-[16/9] skeleton rounded-xl mb-6" />
          <div className="h-8 w-32 skeleton rounded mb-3" />
          <div className="h-6 w-64 skeleton rounded mb-6" />
          <div className="space-y-3">
            <div className="h-4 skeleton rounded" />
            <div className="h-4 skeleton rounded w-3/4" />
            <div className="h-4 skeleton rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-surface-900 text-white flex flex-col items-center justify-center">
        <p className="text-xl font-bold mb-2">Vehicle not found</p>
        <Link to="/browse" className="text-brand-400 hover:text-brand-300 text-sm">Back to browse</Link>
      </div>
    );
  }

  const photos = vehicle.photos ?? [];
  const features: string[] = vehicle.features ? (() => { try { return JSON.parse(vehicle.features); } catch { return []; } })() : [];
  const contactName = vehicle.seller_name || vehicle.dealer_name;
  const contactPhone = vehicle.seller_phone || vehicle.dealer_phone;
  const contactCity = vehicle.seller_city || vehicle.dealer_city;
  const contactState = vehicle.seller_state || vehicle.dealer_state;
  const location = [contactCity, contactState].filter(Boolean).join(', ');
  const description = vehicle.marketplace_description || vehicle.description;

  let marketNote = '';
  if (vehicle.asking_price_cents && vehicle.market_value_cents && vehicle.market_value_cents > 0) {
    const diff = ((vehicle.market_value_cents - vehicle.asking_price_cents) / vehicle.market_value_cents) * 100;
    if (diff > 5) marketNote = `${Math.round(diff)}% below market`;
    else if (diff < -5) marketNote = `${Math.round(Math.abs(diff))}% above market`;
    else marketNote = 'At market value';
  }

  function share() {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: vehicle!.title ?? `${vehicle!.year} ${vehicle!.make} ${vehicle!.model}`, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  return (
    <div className="min-h-screen bg-surface-900 text-white">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-surface-900/95 backdrop-blur-sm border-b border-surface-800/60">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/browse" className="text-surface-400 hover:text-white text-sm font-medium transition-colors">
            &larr; Browse
          </Link>
          <Link to="/" className="text-lg font-bold tracking-tight">AutoCarPost</Link>
          <button onClick={share} className="text-surface-400 hover:text-white text-sm font-medium transition-colors">
            {copied ? 'Copied!' : 'Share'}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto">
        {/* Photo gallery */}
        {photos.length > 0 ? (
          <div className="relative">
            <div className="aspect-[16/9] bg-surface-800">
              <img
                src={photos[photoIdx]?.url}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setPhotoIdx((i) => (i - 1 + photos.length) % photos.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setPhotoIdx((i) => (i + 1) % photos.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
            <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-lg px-2.5 py-1 text-xs text-white/80">
              {photoIdx + 1} / {photos.length}
            </div>
          </div>
        ) : (
          <div className="aspect-[16/9] bg-surface-800 flex items-center justify-center">
            <span className="text-surface-600">No photos</span>
          </div>
        )}

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div className="flex gap-1.5 px-4 py-3 overflow-x-auto no-scrollbar">
            {photos.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setPhotoIdx(i)}
                className={`shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-colors ${
                  i === photoIdx ? 'border-brand-500' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={p.thumbnail_url ?? p.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="px-4 py-6 space-y-6">
          {/* Price + title */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                {vehicle.asking_price_cents && (
                  <p className="text-3xl font-bold text-white">{formatPrice(vehicle.asking_price_cents)}</p>
                )}
                <h1 className="text-xl font-semibold text-white/90 mt-1">
                  {vehicle.year} {vehicle.make} {vehicle.model}{vehicle.trim ? ` ${vehicle.trim}` : ''}
                </h1>
              </div>
              {(vehicle as any).vehicle_badge && (
                <span className="shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold bg-brand-900/50 text-brand-400 border border-brand-700">
                  {(vehicle as any).vehicle_badge}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 text-surface-400 text-sm">
              {vehicle.mileage && <span>{formatMiles(vehicle.mileage)}</span>}
              {location && <span>{location}</span>}
              {marketNote && (
                <span className="text-green-400 font-medium">{marketNote}</span>
              )}
            </div>
          </div>

          {/* Financing Calculator */}
          {vehicle.asking_price_cents && (
            <div>
              <button
                onClick={() => setShowCalc(!showCalc)}
                className="flex items-center gap-2 text-brand-400 text-sm font-medium hover:text-brand-300 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {showCalc ? 'Hide Payment Calculator' : 'Estimate Monthly Payment'}
              </button>
              {showCalc && (() => {
                const principal = Math.max(0, (vehicle.asking_price_cents! / 100) - downPayment);
                const monthlyRate = rate / 100 / 12;
                const payment = monthlyRate > 0
                  ? (principal * monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1)
                  : principal / term;
                return (
                  <div className="mt-3 bg-surface-800 border border-surface-700 rounded-xl p-4 space-y-3">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-white">${Math.round(payment)}<span className="text-lg text-surface-400">/mo</span></p>
                    </div>
                    <div>
                      <label className="text-surface-500 text-xs">Down Payment</label>
                      <input type="number" className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2 text-sm text-white mt-1" placeholder="0"
                        value={downPayment || ''} onChange={(e) => setDownPayment(parseInt(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label className="text-surface-500 text-xs">Interest Rate (%)</label>
                      <input type="number" step="0.1" className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2 text-sm text-white mt-1"
                        value={rate} onChange={(e) => setRate(parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <label className="text-surface-500 text-xs mb-1 block">Loan Term</label>
                      <div className="grid grid-cols-4 gap-1">
                        {[36, 48, 60, 72].map((t) => (
                          <button key={t} onClick={() => setTerm(t)}
                            className={`py-2 rounded-lg text-xs font-medium transition-colors ${term === t ? 'bg-brand-600 text-white' : 'bg-surface-700 text-surface-400'}`}>
                            {t} mo
                          </button>
                        ))}
                      </div>
                    </div>
                    <p className="text-surface-600 text-[10px] text-center">Estimate only. Does not include tax, title, or fees.</p>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Print Forms + Share */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowBosModal(true)}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold bg-surface-800 border border-surface-700 hover:border-brand-500 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Bill of Sale
            </button>
          </div>

          {/* Description */}
          {description && (
            <div>
              <p className="text-white/85 text-base leading-relaxed whitespace-pre-line">{description}</p>
            </div>
          )}

          {/* Highlights */}
          {vehicle.highlights && vehicle.highlights.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Highlights</h3>
              <ul className="space-y-1.5">
                {vehicle.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                    <span className="text-green-400 mt-0.5">+</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Known issues */}
          {vehicle.known_issues && vehicle.known_issues.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Known Issues</h3>
              <ul className="space-y-1.5">
                {vehicle.known_issues.map((issue, i) => (
                  <li key={i} className="flex items-start gap-2 text-white/80 text-sm">
                    <span className="text-yellow-400 mt-0.5">!</span>
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Specs */}
          <div>
            <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Specs</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <SpecChip label="Engine" value={vehicle.engine} />
              <SpecChip label="Transmission" value={vehicle.transmission} />
              <SpecChip label="Drivetrain" value={vehicle.drivetrain} />
              <SpecChip label="Fuel" value={vehicle.fuel_type} />
              <SpecChip label="Body" value={vehicle.body_style} />
              <SpecChip label="Exterior" value={vehicle.exterior_color} />
              <SpecChip label="Interior" value={vehicle.interior_color} />
              <SpecChip label="Condition" value={vehicle.condition} />
              {vehicle.doors && <SpecChip label="Doors" value={String(vehicle.doors)} />}
            </div>
          </div>

          {/* Features */}
          {features.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Features</h3>
              <div className="flex flex-wrap gap-2">
                {features.map((f, i) => (
                  <span key={i} className="bg-surface-800 border border-surface-700 rounded-full px-3 py-1 text-xs text-surface-300">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Seller contact */}
          {(contactName || contactPhone) && (
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Seller</h3>
              {contactName && <p className="text-white font-medium">{contactName}</p>}
              {location && <p className="text-surface-400 text-sm mt-0.5">{location}</p>}
              {contactPhone && (
                showPhone ? (
                  <a
                    href={`tel:${contactPhone}`}
                    className="inline-flex items-center gap-2 mt-3 btn-primary !py-2.5 !text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call {contactPhone}
                  </a>
                ) : (
                  <button
                    onClick={() => setShowPhone(true)}
                    className="inline-flex items-center gap-2 mt-3 btn-primary !py-2.5 !text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Show Phone Number
                  </button>
                )
              )}
            </div>
          )}

          {/* Related listings */}
          {related.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-surface-400 uppercase tracking-wider mb-3">Similar Cars</h3>
              <div className="grid grid-cols-3 gap-2">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/browse/${r.id}`}
                    className="relative aspect-[4/3] rounded-xl overflow-hidden border border-surface-700 hover:border-brand-500 transition-colors"
                  >
                    {r.photo_url ? (
                      <img src={r.photo_url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 bg-surface-800 flex items-center justify-center">
                        <span className="text-surface-600 text-[10px]">No photo</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-2">
                      {r.asking_price_cents && (
                        <p className="text-white font-bold text-xs">{formatPrice(r.asking_price_cents)}</p>
                      )}
                      <p className="text-white/80 text-[10px]">{r.year} {r.make} {r.model}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Print Bill of Sale Modal */}
      {showBosModal && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-surface-800 rounded-t-2xl sm:rounded-2xl p-6 w-full max-w-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Print Bill of Sale</h3>
              <button onClick={() => setShowBosModal(false)} className="text-surface-400 hover:text-white p-1">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-surface-500 text-xs mb-3">Vehicle info is pre-filled. Enter your info to generate a ready-to-sign Bill of Sale.</p>
            <div className="space-y-2">
              <input className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="Your Name *" value={bosBuyer.name} onChange={(e) => setBosBuyer({ ...bosBuyer, name: e.target.value })} />
              <input className="w-full bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="Address" value={bosBuyer.address} onChange={(e) => setBosBuyer({ ...bosBuyer, address: e.target.value })} />
              <div className="flex gap-2">
                <input className="flex-1 bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="City" value={bosBuyer.city} onChange={(e) => setBosBuyer({ ...bosBuyer, city: e.target.value })} />
                <input className="w-16 bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="ST" maxLength={2} value={bosBuyer.state} onChange={(e) => setBosBuyer({ ...bosBuyer, state: e.target.value.toUpperCase() })} />
                <input className="w-20 bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="ZIP" value={bosBuyer.zip} onChange={(e) => setBosBuyer({ ...bosBuyer, zip: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <input className="flex-1 bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="Phone" value={bosBuyer.phone} onChange={(e) => setBosBuyer({ ...bosBuyer, phone: e.target.value })} />
                <input className="flex-1 bg-surface-700 border border-surface-600 rounded-lg px-3 py-2.5 text-sm text-white" placeholder="DL #" value={bosBuyer.drivers_license} onChange={(e) => setBosBuyer({ ...bosBuyer, drivers_license: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (bosBuyer.name) params.set('buyer_name', bosBuyer.name);
                  if (bosBuyer.address) params.set('buyer_address', bosBuyer.address);
                  if (bosBuyer.city) params.set('buyer_city', bosBuyer.city);
                  if (bosBuyer.state) params.set('buyer_state', bosBuyer.state);
                  if (bosBuyer.zip) params.set('buyer_zip', bosBuyer.zip);
                  if (bosBuyer.phone) params.set('buyer_phone', bosBuyer.phone);
                  if (bosBuyer.drivers_license) params.set('buyer_dl', bosBuyer.drivers_license);
                  window.open(`${API}/public/forms/${id}/bill-of-sale?${params.toString()}`, '_blank');
                  setShowBosModal(false);
                }}
                className="flex-1 py-3 rounded-xl text-sm font-semibold bg-brand-600 text-white hover:bg-brand-700 transition-colors"
              >
                Generate & Print
              </button>
              <button onClick={() => setShowBosModal(false)} className="px-4 py-3 rounded-xl text-sm text-surface-400 hover:text-white transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="border-t border-surface-800/60 px-6 py-8 mt-6 text-center">
        <div className="flex items-center justify-center gap-6 mb-4">
          <Link to="/browse" className="text-surface-500 hover:text-white text-xs transition-colors">Browse Cars</Link>
          <Link to="/go" className="text-surface-500 hover:text-white text-xs transition-colors">Sell Your Car</Link>
          <Link to="/login" className="text-surface-500 hover:text-white text-xs transition-colors">Sign In</Link>
        </div>
        <p className="text-surface-600 text-xs">&copy; {new Date().getFullYear()} AutoCarPost</p>
      </footer>
    </div>
  );
}
